import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Divider,
  Tabs,
  Tab,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  AccountBalance,
  CheckCircle,
  Schedule,
  ConfirmationNumber,
  Payment,
} from '@mui/icons-material';
import { useGetBankTransfersQuery, useGetBankTransferStatsQuery, useGetOtherPaymentStatsQuery, useConfirmBankTransferMutation, useExcludeFromStatsMutation, useGetTransactionsQuery, useConfirmPaymentMutation } from '../../store/api/adminApi';

const BankTransfersPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedTransfer, setSelectedTransfer] = useState<any>(null);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [confirmationNotes, setConfirmationNotes] = useState('');
  const [showExcluded, setShowExcluded] = useState(false);
  
  // URLからステータスフィルターとタブを読み取る
  const urlStatus = searchParams.get('status');
  const urlTab = searchParams.get('tab');
  
  const tabValue = useMemo(() => {
    // tabパラメータがある場合はそれを優先
    if (urlTab === 'other') return 1;
    if (urlTab === 'bank') return 0;
    // 後方互換性：古い形式のURLにも対応
    if (urlStatus === 'pending_other' || urlStatus === 'completed_other' || urlStatus === 'all_other') return 1;
    return 0; // 銀行振込タブ
  }, [urlStatus, urlTab]);
  
  // 銀行振込タブのステータスフィルター
  const bankStatusFilter = useMemo(() => {
    if (tabValue === 0) {
      if (urlStatus === 'pending') return 'pending';
      if (urlStatus === 'completed') return 'completed';
      if (urlStatus === 'all') return 'all';
      return 'pending';
    }
    return 'pending';
  }, [urlStatus, tabValue]);
  
  // その他決済タブのステータスフィルター
  const otherStatusFilter = useMemo(() => {
    if (tabValue === 1) {
      if (urlStatus === 'pending_other' || urlStatus === 'pending') return 'pending';
      if (urlStatus === 'completed_other' || urlStatus === 'completed') return 'completed';
      if (urlStatus === 'all_other' || urlStatus === 'all') return 'all';
      return 'pending';
    }
    return 'pending';
  }, [urlStatus, tabValue]);
  
  // タブを変更する関数
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    if (newValue === 0) {
      searchParams.set('tab', 'bank');
      searchParams.set('status', 'pending');
    } else {
      searchParams.set('tab', 'other');
      searchParams.set('status', 'pending');
    }
    setSearchParams(searchParams);
    setPage(0);
  };
  
  // ステータスフィルターを変更する関数
  const setStatusFilterWithUrl = (value: string) => {
    searchParams.set('status', value);
    setSearchParams(searchParams);
  };
  
  // URLパラメータが変更されたらページをリセット
  useEffect(() => {
    setPage(0);
  }, [urlStatus]);

  const { data: transfersData, isLoading, error, refetch } = useGetBankTransfersQuery({
    status: bankStatusFilter,
    page: page + 1,
    limit: rowsPerPage,
    showExcluded: showExcluded ? 'true' : 'false',
  } as any, {
    skip: tabValue !== 0, // 銀行振込タブの場合のみ
  });
  
  // その他決済用のデータ取得（銀行振込を除外）
  const { data: transactionsData, isLoading: isLoadingTransactions, refetch: refetchTransactions } = useGetTransactionsQuery({
    page: page + 1,
    limit: rowsPerPage,
    status: otherStatusFilter,
    excludeBankTransfer: 'true',
    showExcluded: showExcluded ? 'true' : 'false',
  } as any, {
    skip: tabValue !== 1, // その他決済タブの場合のみ
  });

  // 銀行振込の統計
  const { data: statsData } = useGetBankTransferStatsQuery({
    showExcluded: showExcluded ? 'true' : 'false',
  });
  
  // その他決済の統計
  const { data: otherStatsData } = useGetOtherPaymentStatsQuery({
    showExcluded: showExcluded ? 'true' : 'false',
  });
  const [confirmBankTransfer] = useConfirmBankTransferMutation();
  const [excludeFromStats] = useExcludeFromStatsMutation();
  const [confirmPayment] = useConfirmPaymentMutation();

  const transfers = (transfersData as any)?.transfers || [];
  const pagination = (transfersData as any)?.pagination || {};

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleStatusFilterChange = (value: string) => {
    setStatusFilterWithUrl(value);
    setPage(0);
  };
  
  // その他決済の確認処理
  const handleConfirmOtherPayment = async (transactionId: string) => {
    try {
      await confirmPayment({
        id: transactionId,
        confirmed: true
      }).unwrap();
      refetchTransactions();
    } catch (error) {
      console.error('Payment confirmation failed:', error);
    }
  };
  
  // その他決済のデータ（銀行振込のみ除外、統計除外のものも表示）
  const filteredTransactions = useMemo(() => {
    const transactions = (transactionsData as any)?.transactions || [];
    return transactions.filter((t: any) => {
      // 銀行振込は別タブで表示されるため除外
      const isBankTransfer = t.paymentMethod === 'bank_transfer' || t.paymentMethod === 'BANK_TRANSFER';
      if (isBankTransfer) return false;
      return true;
    });
  }, [transactionsData]);
  
  // その他決済の統計除外処理
  const handleExcludeTransactionFromStats = async (transaction: any, exclude: boolean) => {
    try {
      await excludeFromStats({
        type: 'transaction',
        id: transaction.id,
        exclude: exclude
      }).unwrap();
      
      refetchTransactions();
    } catch (error) {
      console.error('Exclude from stats operation failed:', error);
    }
  };

  const handleConfirmTransfer = async () => {
    if (!selectedTransfer) return;

    try {
      await confirmBankTransfer({
        chatId: selectedTransfer.id,
        notes: confirmationNotes
      }).unwrap();
      
      setConfirmDialog(false);
      setSelectedTransfer(null);
      setConfirmationNotes('');
      refetch();
    } catch (error) {
      console.error('Bank transfer confirmation failed:', error);
    }
  };

  const handleExcludeFromStats = async (transfer: any, exclude: boolean) => {
    try {
      await excludeFromStats({
        type: 'chat',
        id: transfer.id,
        exclude: exclude
      }).unwrap();
      
      refetch();
    } catch (error) {
      console.error('Exclude from stats operation failed:', error);
    }
  };

  const getStatusChip = (isCompleted: boolean) => {
    if (isCompleted) {
      return (
        <Chip
          icon={<CheckCircle />}
          label="確認済み"
          color="success"
          size="small"
        />
      );
    }
    return (
      <Chip
        icon={<Schedule />}
        label="未確認"
        color="warning"
        size="small"
      />
    );
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('ja-JP', {
      style: 'currency',
      currency: 'JPY',
    }).format(price);
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return '-';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const isCurrentLoading = tabValue === 0 ? isLoading : isLoadingTransactions;
  const currentError = tabValue === 0 ? error : null;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          <AccountBalance sx={{ mr: 2, verticalAlign: 'middle' }} />
          取引・決済管理
        </Typography>
      </Box>

      {/* タブ */}
      <Paper sx={{ mb: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab 
            icon={<AccountBalance />} 
            iconPosition="start" 
            label={`銀行振込確認 (${statsData?.pending || 0}件待ち)`} 
          />
          <Tab 
            icon={<Payment />} 
            iconPosition="start" 
            label="その他決済確認" 
          />
        </Tabs>
      </Paper>

      {/* ローディング */}
      {isCurrentLoading && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      )}
      
      {/* エラー */}
      {currentError && (
        <Alert severity="error">
          データの取得に失敗しました。
        </Alert>
      )}

      {/* 銀行振込タブ */}
      {tabValue === 0 && !isLoading && !error && (
        <>
          {/* ステータス別の統計カード */}
          <Box display="flex" gap={2} mb={3}>
            <Card sx={{ flex: 1 }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  未確認
                </Typography>
                <Typography variant="h4" color="warning.main">
                  {statsData?.pending || 0}件
                </Typography>
              </CardContent>
            </Card>
            <Card sx={{ flex: 1 }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  確認済み
                </Typography>
                <Typography variant="h4" color="success.main">
                  {statsData?.completed || 0}件
                </Typography>
              </CardContent>
            </Card>
          </Box>

          {/* フィルター */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Box display="flex" alignItems="center" gap={3}>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>ステータス</InputLabel>
                <Select
                  value={bankStatusFilter}
                  label="ステータス"
                  onChange={(e) => handleStatusFilterChange(e.target.value)}
                >
                  <MenuItem value="pending">未確認</MenuItem>
                  <MenuItem value="completed">確認済み</MenuItem>
                  <MenuItem value="all">すべて</MenuItem>
                </Select>
              </FormControl>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showExcluded}
                    onChange={(e) => {
                      setShowExcluded(e.target.checked);
                      setPage(0);
                    }}
                  />
                }
                label="統計除外の取引も表示"
              />
            </Box>
          </Paper>

          {/* 銀行振込一覧テーブル */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>商品</TableCell>
                <TableCell>購入者</TableCell>
                <TableCell>販売者</TableCell>
                <TableCell align="right">金額</TableCell>
                <TableCell>口座名義人名</TableCell>
                <TableCell>作成日時</TableCell>
                <TableCell>ステータス</TableCell>
                <TableCell align="center">統計除外</TableCell>
                <TableCell align="center">アクション</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {transfers.map((transfer: any) => (
                <TableRow key={transfer.id}>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {transfer.product?.title || '商品名不明'}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        ID: {transfer.product?.id || transfer.productId}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2">
                        {transfer.buyer?.name || '購入者不明'}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {transfer.buyer?.email}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2">
                        {transfer.seller?.name || '販売者不明'}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {transfer.seller?.email}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight="bold">
                      {formatPrice(transfer.amount || transfer.product?.price || 0)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {transfer.bankHolderName || '不明'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(transfer.createdAt)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {getStatusChip(transfer.isPaymentCompleted)}
                  </TableCell>
                  <TableCell align="center">
                    {/* 統計除外ボタン */}
                    {transfer.excludeFromStats ? (
                      <Button
                        size="small"
                        variant="outlined"
                        color="success"
                        onClick={() => handleExcludeFromStats(transfer, false)}
                        sx={{ fontSize: '0.7rem', px: 1 }}
                      >
                        統計に含める
                      </Button>
                    ) : (
                      <Button
                        size="small"
                        variant="outlined"
                        color="warning"
                        onClick={() => handleExcludeFromStats(transfer, true)}
                        sx={{ fontSize: '0.7rem', px: 1 }}
                      >
                        統計から除外
                      </Button>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {!transfer.isPaymentCompleted && (
                      <Button
                        size="small"
                        startIcon={<ConfirmationNumber />}
                        onClick={() => {
                          setSelectedTransfer(transfer);
                          setConfirmDialog(true);
                        }}
                      >
                        確認
                      </Button>
                    )}
                    {transfer.isPaymentCompleted && (
                      <Typography variant="caption" color="textSecondary">
                        {formatDate(transfer.paymentConfirmedAt)}に確認済み
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          component="div"
          count={pagination.total || 0}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="表示件数:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}件`}
        />
      </Paper>
        </>
      )}

      {/* その他決済タブ */}
      {tabValue === 1 && !isLoadingTransactions && (
        <>
          {/* ステータス別の統計カード */}
          <Box display="flex" gap={2} mb={3}>
            <Card sx={{ flex: 1 }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  未確認
                </Typography>
                <Typography variant="h4" color="warning.main">
                  {otherStatsData?.pending || 0}件
                </Typography>
              </CardContent>
            </Card>
            <Card sx={{ flex: 1 }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  確認済み
                </Typography>
                <Typography variant="h4" color="success.main">
                  {otherStatsData?.completed || 0}件
                </Typography>
              </CardContent>
            </Card>
          </Box>

          {/* フィルター */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Box display="flex" alignItems="center" gap={3}>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>ステータス</InputLabel>
                <Select
                  value={otherStatusFilter}
                  label="ステータス"
                  onChange={(e) => handleStatusFilterChange(e.target.value)}
                >
                  <MenuItem value="pending">未確認</MenuItem>
                  <MenuItem value="completed">確認済み</MenuItem>
                  <MenuItem value="all">すべて</MenuItem>
                </Select>
              </FormControl>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showExcluded}
                    onChange={(e) => {
                      setShowExcluded(e.target.checked);
                      setPage(0);
                    }}
                  />
                }
                label="統計除外の取引も表示"
              />
            </Box>
          </Paper>

          {filteredTransactions.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="textSecondary">
                {otherStatusFilter === 'pending' ? '確認待ちの取引はありません' : 
                 otherStatusFilter === 'completed' ? '確認済みの取引はありません' : '取引はありません'}
              </Typography>
            </Paper>
          ) : (
            <Paper>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>商品</TableCell>
                      <TableCell>購入者</TableCell>
                      <TableCell>販売者</TableCell>
                      <TableCell align="right">金額</TableCell>
                      <TableCell>決済方法</TableCell>
                      <TableCell>作成日時</TableCell>
                      <TableCell>ステータス</TableCell>
                      <TableCell align="center">統計除外</TableCell>
                      <TableCell align="center">アクション</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredTransactions.map((transaction: any) => (
                      <TableRow key={transaction.id}>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {transaction.productTitle || '商品名不明'}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              ID: {transaction.productId || transaction.id}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2">
                              {transaction.buyerName || '購入者不明'}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {transaction.buyerId}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2">
                              {transaction.sellerName || '販売者不明'}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {transaction.sellerId}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold">
                            {formatPrice(transaction.amount || transaction.finalAmount || 0)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {transaction.paymentMethod || '不明'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(transaction.createdAt)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {transaction.status === 'PAYMENT_CONFIRMED' || transaction.status === 'COMPLETED' ? (
                            <Chip
                              icon={<CheckCircle />}
                              label="確認済み"
                              color="success"
                              size="small"
                            />
                          ) : (
                            <Chip
                              icon={<Schedule />}
                              label="確認待ち"
                              color="warning"
                              size="small"
                            />
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {/* 統計除外ボタン */}
                          {transaction.excludeFromStats ? (
                            <Button
                              size="small"
                              variant="outlined"
                              color="success"
                              onClick={() => handleExcludeTransactionFromStats(transaction, false)}
                              sx={{ fontSize: '0.7rem', px: 1 }}
                            >
                              統計に含める
                            </Button>
                          ) : (
                            <Button
                              size="small"
                              variant="outlined"
                              color="warning"
                              onClick={() => handleExcludeTransactionFromStats(transaction, true)}
                              sx={{ fontSize: '0.7rem', px: 1 }}
                            >
                              統計から除外
                            </Button>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {transaction.status !== 'PAYMENT_CONFIRMED' && transaction.status !== 'COMPLETED' && (
                            <Button
                              size="small"
                              startIcon={<ConfirmationNumber />}
                              onClick={() => handleConfirmOtherPayment(transaction.id)}
                            >
                              確認
                            </Button>
                          )}
                          {(transaction.status === 'PAYMENT_CONFIRMED' || transaction.status === 'COMPLETED') && (
                            <Typography variant="caption" color="textSecondary">
                              {formatDate(transaction.paymentConfirmedAt)}に確認済み
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              <TablePagination
                component="div"
                count={(transactionsData as any)?.total || 0}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="表示件数:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}件`}
              />
            </Paper>
          )}
        </>
      )}

      {/* 確認ダイアログ */}
      <Dialog open={confirmDialog} onClose={() => setConfirmDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>銀行振り込み確認</DialogTitle>
        <DialogContent>
          {selectedTransfer && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" gutterBottom>
                <strong>商品:</strong> {selectedTransfer.product?.title}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>購入者:</strong> {selectedTransfer.buyer?.name}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>金額:</strong> {formatPrice(selectedTransfer.amount || selectedTransfer.product?.price || 0)}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>口座名義人名:</strong> {selectedTransfer.bankHolderName || '不明'}
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Alert severity="info" sx={{ mb: 2 }}>
                銀行振り込みが確認できた場合、この操作を実行してください。
                購入者と販売者に通知が送信されます。
              </Alert>
            </Box>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="確認メモ（任意）"
            type="text"
            fullWidth
            variant="outlined"
            value={confirmationNotes}
            onChange={(e) => setConfirmationNotes(e.target.value)}
            placeholder="振り込み確認に関するメモを入力してください"
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(false)}>
            キャンセル
          </Button>
          <Button onClick={handleConfirmTransfer} color="primary" variant="contained">
            振り込み確認
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BankTransfersPage; 